import { useEffect, useRef, useState } from 'react'
import { Button, Field } from '../ui'
import { fetchMe } from '../lib/api'
import './magic-link.css'

type Phase = 'ready' | 'working' | 'needsCode' | 'done' | 'invalid' | 'missing' | 'retry'

/**
 * Where an emailed sign-in link lands.
 *
 * This page deliberately does NOT spend the token on load. Every customer is a Microsoft 365
 * tenant running Defender for Office 365, whose Safe Links feature fetches urls in mail to scan
 * them before any human clicks. A page that consumed on GET would be consumed by the scanner,
 * and the customer would arrive to be told their link was already used — single use working
 * exactly as designed, product entirely broken.
 *
 * So: arriving here does nothing. A deliberate click POSTs the token. Scanners follow links;
 * they do not press buttons.
 */
export function MagicLinkLanding() {
  const [phase, setPhase] = useState<Phase>('ready')
  const [email, setEmail] = useState<string | null>(null)

  // Whether this session has a workspace, once the session has been asked. Null while unknown,
  // which is not the same as false -- see the Continue button below.
  const [hasWorkspace, setHasWorkspace] = useState<boolean | null>(null)
  const [code, setCode] = useState('')

  // Counted here only to change the wording after a miss. The real limit is in the database,
  // which retires the link after five wrong codes — a client-side counter protects nothing.
  const [tries, setTries] = useState(0)

  // A code being checked. The form stays on screen meanwhile (it used to unmount into the
  // 'working' view, taking the typed code and the keyboard focus with it); only the button changes.
  const [checking, setChecking] = useState(false)

  // Held in a ref rather than state so the token never lands in a React tree that a devtools
  // extension or an error reporter might serialise.
  const token = useRef<string | null>(null)

  // What the attempt that could not get an answer was, so "Try again" repeats exactly that.
  const retryOf = useRef<{ phase: 'ready' | 'needsCode'; code?: string }>({ phase: 'ready' })

  // One consume request at a time. Two in flight race on the same single-use link: one signs the
  // person in, the other gets the uniform refusal -- and if that answer lands last it replaced
  // "signed in" with "that code did not match". A ref, so a second click in the same frame sees it.
  const inFlight = useRef(false)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const value = params.get('token')

    if (!value) {
      setPhase('missing')
      return
    }

    token.current = value

    // Strip it from the address bar immediately — before the user can copy the URL, before it
    // reaches browser history, and before any outbound request can carry it in a Referer
    // header. replaceState rather than pushState, so Back does not restore it.
    window.history.replaceState({}, '', window.location.pathname)
  }, [])

  async function signIn(withCode?: string) {
    if (!token.current || inFlight.current) return
    inFlight.current = true

    // A retry is the attempt it repeats, so a refusal lands where that attempt's would have.
    const attempted = phase === 'retry' ? retryOf.current.phase : phase
    if (attempted === 'needsCode') setChecking(true)
    else setPhase('working')

    // NO ANSWER IS NOT A REFUSAL. A dropped connection (a throw) or a server error used to be read
    // as a dead link: the token was thrown away and the person told to request a new one, although
    // the link may never have reached the server. Keep the token and offer the same attempt again.
    // Not a claim that the link is unused -- the request may have landed before the answer was lost.
    const noAnswer = () => {
      retryOf.current = { phase: attempted === 'needsCode' ? 'needsCode' : 'ready', code: withCode }
      setChecking(false)
      setPhase('retry')
    }

    try {
      const response = await fetch('/api/auth/magic-link/consume', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Carries the refresh cookie back, and carries the binding cookie out — the browser
        // that requested this link holds one, and is admitted without a code.
        credentials: 'same-origin',
        body: JSON.stringify({ token: token.current, code: withCode ?? null }),
      })

      if (response.status >= 500) {
        noAnswer()
        return
      }

      if (!response.ok) {
        // The server answers the same way for an unknown link, an expired one, a spent one and
        // a wrong code, so this branch cannot tell them apart — and must not pretend to.
        //
        // The first refusal is therefore treated as "this is probably a different device", which
        // is the common and recoverable case. Only after a code has actually been tried is the
        // link called dead. Being wrong about that costs a customer one extra screen; the other
        // way round tells someone their link is broken when they simply needed to type six
        // characters.
        if (attempted === 'ready') {
          setPhase('needsCode')
          return
        }

        setTries((n) => n + 1)
        setChecking(false)
        setPhase(tries >= 4 ? 'invalid' : 'needsCode')
        return
      }

      setChecking(false)

      const body = (await response.json()) as { email?: string }
      setEmail(body.email ?? null)
      setPhase('done')

      // WHERE DOES THIS PERSON GO NEXT.
      //
      // Signing in and having somewhere to land are different questions, and this page used to
      // ask only the first. Continue sent everybody to '/', which for a session with no
      // workspace bounces to the sign-in page -- so a first-time magic-link user completed a
      // sign-in that worked perfectly and arrived back where they started, with nothing on
      // screen to explain it. Seen on the first real magic link this deployment ever sent.
      //
      // EntraComplete has asked this question since it was written; this page simply never did.
      // Asked here rather than on mount because a session that exists before the link is
      // consumed belongs to somebody else's earlier sign-in.
      // fetchMe, not getMe: getMe turns "could not ask" into "no session" and never rejects, so the
      // unknown branch below could never be reached and a blip here read as "no workspace".
      void fetchMe().then((r) => setHasWorkspace(r.ok && r.data ? !!r.data.workspaceId : null))

      // Only now is the token certainly spent.
      token.current = null
    } catch {
      noAnswer()
    } finally {
      inFlight.current = false
    }
  }

  // Back to the sign-in page for a fresh link. Not '/', which for a signed-out visitor is a detour
  // through the session check to the same place.
  function requestNewLink() {
    window.location.href = '/auth/sign-in'
  }

  return (
    <main className="magic-link rise-in">
      <div className="label-mono">Defenssive AI Security Manager</div>

      {phase === 'missing' ? (
        <>
          <h2>This link is incomplete</h2>
          <p className="text-muted">
            Open the sign-in link from your email again, or request a new one.
          </p>
          <Button variant="primary" onClick={requestNewLink}>
            Request a new link
          </Button>
        </>
      ) : phase === 'retry' ? (
        <>
          <h2>We could not confirm that sign-in</h2>
          <p className="text-muted" role="alert">
            Defenssive did not answer. Try again — if the link turns out to have been used already,
            request a new one.
          </p>
          <Button
            variant="primary"
            size="lg"
            disabled={checking}
            onClick={() => void signIn(retryOf.current.code)}
          >
            {checking ? 'Checking…' : 'Try again'}
          </Button>
          <Button variant="ghost" onClick={requestNewLink}>
            Request a new link
          </Button>
        </>
      ) : phase === 'invalid' ? (
        <>
          <h2>That sign-in link is no longer valid</h2>
          <p className="text-muted">
            Links work once and expire after 15 minutes. Requesting a new one also replaces any
            earlier link.
          </p>
          <Button variant="primary" onClick={requestNewLink}>
            Request a new link
          </Button>
        </>
      ) : phase === 'needsCode' ? (
        <>
          <h2>Enter your confirmation code</h2>
          {/* NOT stated as fact. The server answers identically for "different device" and "expired
              or replaced link", and the second is the more common on the same device -- a link older
              than 15 minutes, or one replaced by Resend. Both are named, and both have a way out. */}
          <p className="text-muted">
            If you asked for this link on another device, enter the six-character code shown there.
            If you are on the same device, this link has probably expired (links last 15 minutes) or
            been replaced by a newer one — request a new link instead.
          </p>
          <form
            onSubmit={(event) => {
              event.preventDefault()
              void signIn(code)
            }}
          >
            <Field
              label="Confirmation code"
              value={code}
              autoComplete="one-time-code"
              inputMode="text"
              spellCheck={false}
              maxLength={12}
              onChange={(event) => setCode(event.target.value)}
              error={tries > 0 ? 'That code did not match. Check the screen that requested the link.' : undefined}
            />
            <Button
              type="submit"
              variant="primary"
              size="lg"
              disabled={checking || code.trim().length < 6}
              className="magic-link-submit"
            >
              {checking ? 'Checking…' : 'Continue'}
            </Button>
          </form>
          <Button variant="ghost" onClick={requestNewLink} disabled={checking}>
            Request a new link
          </Button>
          <p className="footnote text-muted">
            The code is only ever shown on the screen that requested the link — never in the
            email. That is what keeps a copy of the link from being enough to sign in as you.
          </p>
        </>
      ) : phase === 'done' ? (
        <>
          <h2>You&rsquo;re signed in</h2>
          <p className="text-muted">
            {email ? <>Signed in as {email}.</> : null} Nothing in your Microsoft tenant has
            changed.
          </p>
          {/*
            Three states, and the third is not a mistake. Null means the session has not answered
            yet -- sending someone to /connect on a guess would show a first-time screen to a
            customer whose tenant is already connected, so an unanswered session goes to '/',
            which is where this always went.
          */}
          {hasWorkspace === true ? (
            <Button
              variant="primary"
              onClick={() => (window.location.href = '/dashboard')}
            >
              Go to your dashboard
            </Button>
          ) : hasWorkspace === false ? (
            <>
              {/* Not "connect your tenant": a session with no workspace is also somebody in several
                  workspaces, or with an invitation waiting. /connect shows whichever applies. */}
              <p className="text-muted">
                Next, open a workspace — or, if this is your first sign-in, set one up.
              </p>
              <Button
                variant="primary"
                onClick={() => (window.location.href = '/connect')}
              >
                Continue
              </Button>
            </>
          ) : (
            <Button variant="primary" onClick={() => (window.location.href = '/')}>
              Continue
            </Button>
          )}
        </>
      ) : (
        <>
          <h2>Continue signing in</h2>
          <p className="text-muted">
            Select continue to finish signing in to Defenssive. This link works once and expires
            15 minutes after it was sent.
          </p>
          <Button
            variant="primary"
            size="lg"
            disabled={phase === 'working'}
            onClick={() => void signIn()}
          >
            {phase === 'working' ? 'Signing in…' : 'Continue'}
          </Button>
          <p className="footnote text-muted">
            You are asked to confirm because automated security scanners follow links in email.
            Requiring a click keeps them from using your sign-in link before you do.
          </p>
        </>
      )}
    </main>
  )
}
